import React, { useEffect, useState, createContext } from "react";
import lbl from "../data/Labels.json";
import {
  buildCurrentUser,
  clearSession,
  getToken,
  isTokenExpired,
} from "../services/session";
import { setUserPositionId } from "../utils/financeAccess";
import db from "../services/query";
import { resolveApiBase } from "../services/apiBase";

export const AuthContext = createContext();
AuthContext.displayName = "CurrentUserContext";

const API_BASE = resolveApiBase();

async function probeToken(token) {
  const res = await fetch(`${API_BASE.replace(/\/$/, "")}/dropdowns/halfYears`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401 || res.status === 403) {
    return false;
  }
  return res.ok || res.status === 200;
}

function profileMustChangePassword(profile) {
  return Number(profile?.must_change_password) === 1 || profile?.must_change_password === true;
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [accountProfile, setAccountProfile] = useState(null);
  const [pending, setPending] = useState(true);

  const refresh = async () => {
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      if (token) clearSession();
      setCurrentUser(null);
      setAccountProfile(null);
      setPending(false);
      return;
    }

    const loadProfile = async () => {
      try {
        const res = await db.getAll("login");
        const profile = res?.data?.data?.[0] || null;
        setAccountProfile(profile);
        if (profile?.position_id != null) {
          setUserPositionId(profile.position_id);
        }
        return profile;
      } catch (_profileErr) {
        setAccountProfile(null);
        return null;
      }
    };

    try {
      const ok = await probeToken(token);
      if (!ok) {
        clearSession();
        setCurrentUser(null);
        setAccountProfile(null);
        if (window.location.pathname.indexOf("/login") === -1) {
          window.location.replace("/login.html");
        }
        return;
      }
      setCurrentUser(buildCurrentUser());
      await loadProfile();
    } catch (_e) {
      setCurrentUser(buildCurrentUser());
      await loadProfile();
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    refresh();
    const onAuth = () => {
      setPending(true);
      refresh();
    };
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith("aria_")) {
        setPending(true);
        refresh();
      }
    };
    window.addEventListener("aria-auth-changed", onAuth);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("aria-auth-changed", onAuth);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const logOut = () => {
    clearSession();
    setUserPositionId(null);
    setCurrentUser(null);
    setAccountProfile(null);
    window.dispatchEvent(new Event("aria-auth-changed"));
  };

  if (pending) return <div className="loading">{lbl.Loading}</div>;

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        accountProfile,
        mustChangePassword: profileMustChangePassword(accountProfile),
        logOut,
        refreshAuth: refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
